import { useState, useCallback } from "react";
import moment from "moment";

export type RangeSelectionPhase = "idle" | "start-selected";

export interface CalendarRangeState {
    rangeStart: string;
    rangeEnd: string;
    phase: RangeSelectionPhase;
}

export interface CalendarRangeActions {
    setRangeStart: (date: string) => void;
    setRangeEnd: (date: string) => void;
    setRangeBoth: (start: string, end: string) => void;
    handleCalendarDateClick: (dateKey: string) => void;
    resetRange: () => void;
}

export function computeCalendarDateClick(
    dateKey: string,
    currentState: CalendarRangeState,
): CalendarRangeState {
    const today = moment().format("YYYY-MM-DD");
    if (dateKey < today) return currentState;

    const { phase, rangeStart } = currentState;

    if (phase === "idle" || !rangeStart) {
        return {
            rangeStart: dateKey,
            rangeEnd: "",
            phase: "start-selected",
        };
    }

    if (phase === "start-selected") {
        if (dateKey === rangeStart) {
            return {
                rangeStart: "",
                rangeEnd: "",
                phase: "idle",
            };
        }

        if (dateKey < rangeStart) {
            return {
                rangeStart: dateKey,
                rangeEnd: rangeStart,
                phase: "idle",
            };
        }

        return {
            rangeStart,
            rangeEnd: dateKey,
            phase: "idle",
        };
    }

    return {
        rangeStart: dateKey,
        rangeEnd: "",
        phase: "start-selected",
    };
}

export function computeDropdownStartChange(
    newStart: string,
    currentEnd: string,
): CalendarRangeState {
    if (currentEnd && newStart > currentEnd) {
        return {
            rangeStart: newStart,
            rangeEnd: "",
            phase: "start-selected",
        };
    }
    return {
        rangeStart: newStart,
        rangeEnd: currentEnd,
        phase: currentEnd ? "idle" : "start-selected",
    };
}

export function computeDropdownEndChange(
    currentStart: string,
    newEnd: string,
): CalendarRangeState {
    if (currentStart && newEnd < currentStart) {
        return {
            rangeStart: newEnd,
            rangeEnd: currentStart,
            phase: "idle",
        };
    }
    return {
        rangeStart: currentStart,
        rangeEnd: newEnd,
        phase: currentStart ? "idle" : "idle",
    };
}

export function useCalendarRangeSelection(): [CalendarRangeState, CalendarRangeActions] {
    const [rangeStart, setRangeStartRaw] = useState("");
    const [rangeEnd, setRangeEndRaw] = useState("");
    const [phase, setPhase] = useState<RangeSelectionPhase>("idle");

    const applyState = useCallback((s: CalendarRangeState) => {
        setRangeStartRaw(s.rangeStart);
        setRangeEndRaw(s.rangeEnd);
        setPhase(s.phase);
    }, []);

    const setRangeStart = useCallback((date: string) => {
        const result = computeDropdownStartChange(date, rangeEnd);
        applyState(result);
    }, [rangeEnd, applyState]);

    const setRangeEnd = useCallback((date: string) => {
        const result = computeDropdownEndChange(rangeStart, date);
        applyState(result);
    }, [rangeStart, applyState]);

    const handleCalendarDateClick = useCallback((dateKey: string) => {
        const current: CalendarRangeState = { rangeStart, rangeEnd, phase };
        const next = computeCalendarDateClick(dateKey, current);
        applyState(next);
    }, [rangeStart, rangeEnd, phase, applyState]);

    const setRangeBoth = useCallback((start: string, end: string) => {
        applyState({ rangeStart: start, rangeEnd: end, phase: end ? "idle" : "start-selected" });
    }, [applyState]);

    const resetRange = useCallback(() => {
        applyState({ rangeStart: "", rangeEnd: "", phase: "idle" });
    }, [applyState]);

    return [
        { rangeStart, rangeEnd, phase },
        { setRangeStart, setRangeEnd, setRangeBoth, handleCalendarDateClick, resetRange },
    ];
}
